/**
 * Crafting UI
 *
 * Interface for item crafting and enhancement
 */

import { Renderer } from '@client/core/Renderer';
import { Inventory } from '@client/inventory/Inventory';
import { EquipmentItem } from '@shared/types';
import { getItemColor } from '@shared/items/Item';
import { t } from '@locales/index';
import {
  CraftingSystem,
  CraftingCurrencyType,
  CRAFTING_CURRENCIES,
  ENHANCEMENT_RATES,
  ENHANCEMENT_BONUSES,
} from '@shared/crafting/CraftingSystem';

type CraftingTab = 'craft' | 'enhance';

export class CraftingUI {
  private inventory: Inventory;
  private screenWidth: number;
  private screenHeight: number;
  private isOpen: boolean = false;
  private fadeAlpha: number = 0;

  // Selected item for crafting
  private selectedItem: EquipmentItem | null = null;
  private selectedItemIndex: number = -1;

  // UI state
  private activeTab: CraftingTab = 'craft';
  private hoveredCurrency: CraftingCurrencyType | null = null;
  private resultMessage: string = '';
  private resultSuccess: boolean = false;
  private resultTimer: number = 0;

  // Animation
  private craftAnimTimer: number = 0;
  private isAnimating: boolean = false;

  // Lock state for sabitleme_tasi
  private lockPrefixes: boolean = false;
  private lockSuffixes: boolean = false;

  // Panel dimensions
  private readonly PANEL_WIDTH = 600;
  private readonly PANEL_HEIGHT = 500;

  constructor(inventory: Inventory, screenWidth: number, screenHeight: number) {
    this.inventory = inventory;
    this.screenWidth = screenWidth;
    this.screenHeight = screenHeight;
  }

  toggle(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.fadeAlpha = 0;
    } else {
      this.selectedItem = null;
      this.selectedItemIndex = -1;
    }
  }

  open(): void {
    this.isOpen = true;
    this.fadeAlpha = 0;
  }

  close(): void {
    this.isOpen = false;
    this.selectedItem = null;
    this.selectedItemIndex = -1;
  }

  isVisible(): boolean {
    return this.isOpen;
  }

  setScreenSize(width: number, height: number): void {
    this.screenWidth = width;
    this.screenHeight = height;
  }

  update(deltaTime: number): void {
    if (this.resultTimer > 0) {
      this.resultTimer -= deltaTime;
    }

    if (this.isAnimating) {
      this.craftAnimTimer += deltaTime;
      if (this.craftAnimTimer >= 0.5) {
        this.isAnimating = false;
        this.craftAnimTimer = 0;
      }
    }
  }

  private getCurrencyCount(type: CraftingCurrencyType): number {
    return this.inventory.getCraftingCurrencyCount(type);
  }

  private consumeCurrency(type: CraftingCurrencyType, amount: number): boolean {
    return this.inventory.removeCraftingCurrency(type, amount);
  }

  handleMouseMove(x: number, y: number): void {
    if (!this.isOpen) return;

    // Check currency hover
    this.hoveredCurrency = this.getCurrencyAtPosition(x, y);
  }

  handleClick(x: number, y: number): void {
    if (!this.isOpen) return;

    const panelX = (this.screenWidth - this.PANEL_WIDTH) / 2;
    const panelY = (this.screenHeight - this.PANEL_HEIGHT) / 2;

    // Tab clicks
    if (y >= panelY + 10 && y <= panelY + 40) {
      if (x >= panelX + 20 && x <= panelX + 120) {
        this.activeTab = 'craft';
      } else if (x >= panelX + 130 && x <= panelX + 230) {
        this.activeTab = 'enhance';
      }
    }

    // Item selection area
    const itemAreaY = panelY + 60;
    if (y >= itemAreaY && y <= itemAreaY + 200) {
      this.handleItemSelection(x, y, panelX, itemAreaY);
    }

    // Currency/enhance button clicks
    if (this.selectedItem) {
      if (this.activeTab === 'craft') {
        this.handleCraftClick(x, y, panelX, panelY);
      } else {
        this.handleEnhanceClick(x, y, panelX, panelY);
      }
    }
  }

  private handleItemSelection(x: number, y: number, panelX: number, areaY: number): void {
    const slotSize = 48;
    const padding = 4;
    const cols = 10;
    const startX = panelX + 20;

    const items = this.inventory.getAllItems();

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (!item || !('slot' in item)) continue; // Only equipment

      const col = i % cols;
      const row = Math.floor(i / cols);
      const slotX = startX + col * (slotSize + padding);
      const slotY = areaY + row * (slotSize + padding);

      if (x >= slotX && x <= slotX + slotSize && y >= slotY && y <= slotY + slotSize) {
        this.selectedItem = item as EquipmentItem;
        this.selectedItemIndex = i;
        return;
      }
    }
  }

  private handleCraftClick(x: number, y: number, panelX: number, panelY: number): void {
    const currencyY = panelY + 280;
    const currencies: CraftingCurrencyType[] = [
      'demir_parcasi', 'ruh_damlasi', 'kam_boyasi',
      'bereket_tozu', 'ugur_tozu', 'temizleme_suyu',
      'sabitleme_tasi', 'kutsal_yag',
    ];

    const buttonWidth = 80;
    const buttonHeight = 60;
    const spacing = 10;

    for (let i = 0; i < currencies.length; i++) {
      const col = i % 3;
      const row = Math.floor(i / 3);
      const btnX = panelX + 40 + col * (buttonWidth + spacing);
      const btnY = currencyY + row * (buttonHeight + spacing);

      if (x >= btnX && x <= btnX + buttonWidth && y >= btnY && y <= btnY + buttonHeight) {
        this.applyCurrency(currencies[i]);
        return;
      }
    }
  }

  private handleEnhanceClick(x: number, y: number, panelX: number, panelY: number): void {
    const buttonY = panelY + 350;
    const buttonWidth = 150;
    const buttonHeight = 50;

    // Regular enhance button
    const btn1X = panelX + 100;
    if (x >= btn1X && x <= btn1X + buttonWidth && y >= buttonY && y <= buttonY + buttonHeight) {
      this.enhanceItem(false, false);
      return;
    }

    // Protected enhance button
    const btn2X = panelX + 280;
    if (x >= btn2X && x <= btn2X + buttonWidth && y >= buttonY && y <= buttonY + buttonHeight) {
      this.enhanceItem(true, false);
      return;
    }

    // Hammer enhance button
    const btn3X = panelX + 190;
    const btn3Y = buttonY + 60;
    if (x >= btn3X && x <= btn3X + buttonWidth && y >= btn3Y && y <= btn3Y + buttonHeight) {
      this.enhanceItem(false, true);
      return;
    }
  }

  private applyCurrency(type: CraftingCurrencyType): void {
    if (!this.selectedItem) return;

    const amount = this.getCurrencyCount(type);
    if (amount <= 0) {
      this.showResult(t('crafting.notEnoughMaterials'), false);
      return;
    }

    let result;
    switch (type) {
      case 'demir_parcasi':
        result = CraftingSystem.applyDemirParcasi(this.selectedItem);
        break;
      case 'ruh_damlasi':
        result = CraftingSystem.applyRuhDamlasi(this.selectedItem);
        break;
      case 'kam_boyasi':
        // If locks are active, consume a sabitleme_tasi as well
        if (this.lockPrefixes || this.lockSuffixes) {
          const lockAmount = this.getCurrencyCount('sabitleme_tasi');
          if (lockAmount <= 0) {
            this.showResult(t('crafting.lockStoneRequired'), false);
            this.lockPrefixes = false;
            this.lockSuffixes = false;
            return;
          }
          this.consumeCurrency('sabitleme_tasi', 1);
        }
        result = CraftingSystem.applyKamBoyasi(this.selectedItem, this.lockPrefixes, this.lockSuffixes);
        // Reset locks after use
        this.lockPrefixes = false;
        this.lockSuffixes = false;
        break;
      case 'bereket_tozu':
        result = CraftingSystem.applyBereketTozu(this.selectedItem);
        break;
      case 'ugur_tozu':
        result = CraftingSystem.applyUgurTozu(this.selectedItem);
        break;
      case 'temizleme_suyu':
        result = CraftingSystem.applyTemizlemeSuyu(this.selectedItem);
        break;
      case 'sabitleme_tasi':
        // Toggle lock mode: cycles through none → lock prefixes → lock suffixes → none
        if (!this.lockPrefixes && !this.lockSuffixes) {
          this.lockPrefixes = true;
          this.showResult(t('crafting.prefixesLocked'), true);
        } else if (this.lockPrefixes) {
          this.lockPrefixes = false;
          this.lockSuffixes = true;
          this.showResult(t('crafting.suffixesLocked'), true);
        } else {
          this.lockSuffixes = false;
          this.showResult(t('crafting.lockRemoved'), false);
        }
        return; // Don't consume currency yet - consumed on actual craft
      case 'kutsal_yag':
        result = CraftingSystem.applyKutsalYag(this.selectedItem);
        break;
      default:
        return;
    }

    if (result.success) {
      this.consumeCurrency(type, 1);
      this.isAnimating = true;
    }

    this.showResult(result.message, result.success);
  }

  private enhanceItem(useProtection: boolean, useHammer: boolean): void {
    if (!this.selectedItem) return;

    // Check for required currencies
    if (useProtection) {
      const protAmount = this.getCurrencyCount('koruma_muhru');
      if (protAmount <= 0) {
        this.showResult(t('crafting.protectionMissing'), false);
        return;
      }
    }

    if (useHammer) {
      const hammerAmount = this.getCurrencyCount('guclu_cekic');
      if (hammerAmount <= 0) {
        this.showResult(t('crafting.hammerMissing'), false);
        return;
      }
    }

    const result = CraftingSystem.enhance(this.selectedItem, useProtection, useHammer);

    // Consume currencies
    if (useProtection) {
      this.consumeCurrency('koruma_muhru', 1);
    }
    if (useHammer) {
      this.consumeCurrency('guclu_cekic', 1);
    }

    if (result.destroyed) {
      // Remove item from inventory
      this.inventory.removeItem(this.selectedItemIndex);
      this.selectedItem = null;
      this.selectedItemIndex = -1;
    }

    this.isAnimating = true;
    this.showResult(result.message, result.success);
  }

  private showResult(message: string, success: boolean): void {
    this.resultMessage = message;
    this.resultSuccess = success;
    this.resultTimer = 2;
  }

  private getCurrencyAtPosition(x: number, y: number): CraftingCurrencyType | null {
    if (this.activeTab !== 'craft') return null;

    const panelX = (this.screenWidth - this.PANEL_WIDTH) / 2;
    const panelY = (this.screenHeight - this.PANEL_HEIGHT) / 2;
    const currencyY = panelY + 280;

    const currencies: CraftingCurrencyType[] = [
      'demir_parcasi', 'ruh_damlasi', 'kam_boyasi',
      'bereket_tozu', 'ugur_tozu', 'temizleme_suyu',
      'sabitleme_tasi', 'kutsal_yag',
    ];

    const buttonWidth = 80;
    const buttonHeight = 60;
    const spacing = 10;

    for (let i = 0; i < currencies.length; i++) {
      const col = i % 3;
      const row = Math.floor(i / 3);
      const btnX = panelX + 40 + col * (buttonWidth + spacing);
      const btnY = currencyY + row * (buttonHeight + spacing);

      if (x >= btnX && x <= btnX + buttonWidth && y >= btnY && y <= btnY + buttonHeight) {
        return currencies[i];
      }
    }

    return null;
  }

  addCurrency(type: CraftingCurrencyType, amount: number): void {
    this.inventory.addCraftingCurrency(type, amount);
  }

  render(renderer: Renderer): void {
    if (!this.isOpen) return;

    // Animate fade-in
    if (this.fadeAlpha < 1) {
      this.fadeAlpha = Math.min(1, this.fadeAlpha + 0.08);
    }

    const ctx = renderer.context;
    ctx.save();
    ctx.globalAlpha = this.fadeAlpha;

    const panelX = (this.screenWidth - this.PANEL_WIDTH) / 2;
    const panelY = (this.screenHeight - this.PANEL_HEIGHT) / 2;

    // Dark overlay
    ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
    ctx.fillRect(0, 0, this.screenWidth, this.screenHeight);

    // Main panel
    ctx.fillStyle = 'rgba(25, 22, 18, 0.98)';
    ctx.fillRect(panelX, panelY, this.PANEL_WIDTH, this.PANEL_HEIGHT);

    ctx.strokeStyle = '#4d4439';
    ctx.lineWidth = 3;
    ctx.strokeRect(panelX, panelY, this.PANEL_WIDTH, this.PANEL_HEIGHT);

    // Title
    ctx.fillStyle = '#ffd700';
    ctx.font = 'bold 20px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(t('crafting.title'), panelX + this.PANEL_WIDTH / 2, panelY - 10);

    // Tabs
    this.renderTabs(ctx, panelX, panelY);

    // Content based on tab
    if (this.activeTab === 'craft') {
      this.renderCraftTab(ctx, panelX, panelY);
    } else {
      this.renderEnhanceTab(ctx, panelX, panelY);
    }

    // Selected item display
    this.renderSelectedItem(ctx, panelX, panelY);

    // Result message
    if (this.resultTimer > 0) {
      this.renderResultMessage(ctx, panelX, panelY);
    }

    // Animation overlay
    if (this.isAnimating) {
      this.renderCraftAnimation(ctx, panelX, panelY);
    }

    // Close hint
    ctx.fillStyle = '#666666';
    ctx.font = '12px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(t('crafting.closeHint'), panelX + this.PANEL_WIDTH / 2, panelY + this.PANEL_HEIGHT + 20);

    ctx.restore(); // restore globalAlpha from fade-in
  }

  private renderTabs(ctx: CanvasRenderingContext2D, panelX: number, panelY: number): void {
    const tabs = [
      { id: 'craft', label: 'Craft', x: 20 },
      { id: 'enhance', label: t('crafting.enhance'), x: 130 },
    ];

    for (const tab of tabs) {
      const isActive = this.activeTab === tab.id;

      ctx.fillStyle = isActive ? '#3d3429' : '#1a1815';
      ctx.fillRect(panelX + tab.x, panelY + 10, 100, 30);

      ctx.strokeStyle = isActive ? '#ffd700' : '#3d3429';
      ctx.lineWidth = 2;
      ctx.strokeRect(panelX + tab.x, panelY + 10, 100, 30);

      ctx.fillStyle = isActive ? '#ffd700' : '#888888';
      ctx.font = '14px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(tab.label, panelX + tab.x + 50, panelY + 30);
    }
  }

  private renderCraftTab(ctx: CanvasRenderingContext2D, panelX: number, panelY: number): void {
    // Item selection area
    this.renderItemSelection(ctx, panelX, panelY + 50);

    // Currency buttons
    const currencyY = panelY + 280;
    ctx.fillStyle = '#aaaaaa';
    ctx.font = '14px Arial';
    ctx.textAlign = 'left';
    ctx.fillText(t('crafting.materials'), panelX + 20, currencyY - 10);

    const currencies: CraftingCurrencyType[] = [
      'demir_parcasi', 'ruh_damlasi', 'kam_boyasi',
      'bereket_tozu', 'ugur_tozu', 'temizleme_suyu',
      'sabitleme_tasi', 'kutsal_yag',
    ];

    const buttonWidth = 80;
    const buttonHeight = 60;
    const spacing = 10;

    for (let i = 0; i < currencies.length; i++) {
      const type = currencies[i];
      const info = CRAFTING_CURRENCIES[type];
      const amount = this.getCurrencyCount(type);

      const col = i % 3;
      const row = Math.floor(i / 3);
      const btnX = panelX + 40 + col * (buttonWidth + spacing);
      const btnY = currencyY + row * (buttonHeight + spacing);

      const isHovered = this.hoveredCurrency === type;

      // Button background
      ctx.fillStyle = isHovered ? '#3a3025' : (amount > 0 ? '#2a2520' : '#1a1510');
      ctx.fillRect(btnX, btnY, buttonWidth, buttonHeight);

      ctx.strokeStyle = isHovered ? '#ffd700' : (amount > 0 ? '#4d4439' : '#2d241c');
      ctx.lineWidth = isHovered ? 2 : 1;
      ctx.strokeRect(btnX, btnY, buttonWidth, buttonHeight);

      // Icon
      ctx.font = '20px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(info.icon, btnX + buttonWidth / 2, btnY + 25);

      // Amount
      ctx.fillStyle = amount > 0 ? '#ffffff' : '#666666';
      ctx.font = '10px Arial';
      ctx.fillText(`x${amount}`, btnX + buttonWidth / 2, btnY + buttonHeight - 8);

      // Name (abbreviated)
      ctx.fillStyle = '#aaaaaa';
      ctx.font = '9px Arial';
      const shortName = info.name.split(' ')[0];
      ctx.fillText(shortName, btnX + buttonWidth / 2, btnY + 42);
    }

    // Lock state indicator
    if (this.lockPrefixes || this.lockSuffixes) {
      const lockText = this.lockPrefixes ? `🔒 ${t('crafting.prefixesLockedIndicator')}` : `🔒 ${t('crafting.suffixesLockedIndicator')}`;
      ctx.fillStyle = '#ffaa00';
      ctx.font = 'bold 12px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(lockText, panelX + this.PANEL_WIDTH / 2, panelY + this.PANEL_HEIGHT - 55);
    }

    // Description for hovered currency
    if (this.hoveredCurrency) {
      const info = CRAFTING_CURRENCIES[this.hoveredCurrency];
      ctx.fillStyle = '#888888';
      ctx.font = '12px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(info.description, panelX + this.PANEL_WIDTH / 2, panelY + this.PANEL_HEIGHT - 35);
    }
  }

  private renderEnhanceTab(ctx: CanvasRenderingContext2D, panelX: number, panelY: number): void {
    // Item selection area
    this.renderItemSelection(ctx, panelX, panelY + 50);

    if (!this.selectedItem) {
      ctx.fillStyle = '#666666';
      ctx.font = '16px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(t('crafting.selectItem'), panelX + this.PANEL_WIDTH / 2, panelY + 300);
      return;
    }

    // Enhancement info
    const currentLevel = this.selectedItem.enhancement || 0;
    const nextLevel = currentLevel + 1;
    const rates = ENHANCEMENT_RATES[currentLevel];

    ctx.fillStyle = '#aaaaaa';
    ctx.font = '14px Arial';
    ctx.textAlign = 'left';

    const infoX = panelX + 350;
    const infoY = panelY + 280;

    ctx.fillText(t('crafting.currentLevel', { level: String(currentLevel) }), infoX, infoY);

    if (currentLevel < 15) {
      ctx.fillText(t('crafting.targetLevel', { level: String(nextLevel) }), infoX, infoY + 20);
      ctx.fillStyle = '#44aa44';
      ctx.fillText(t('crafting.successRate', { rate: String(rates.success) }), infoX, infoY + 45);

      if (rates.destroy > 0) {
        ctx.fillStyle = '#aa4444';
        ctx.fillText(t('crafting.breakRate', { rate: String(rates.destroy) }), infoX, infoY + 65);
      }

      if (rates.downgrade > 0) {
        ctx.fillStyle = '#aaaa44';
        ctx.fillText(t('crafting.downgradeRate', { rate: String(rates.downgrade) }), infoX, infoY + 85);
      }

      // Bonus info
      const currentBonus = ENHANCEMENT_BONUSES[currentLevel];
      const nextBonus = ENHANCEMENT_BONUSES[nextLevel];
      ctx.fillStyle = '#88ff88';
      ctx.fillText(`Bonus: x${currentBonus.toFixed(2)} → x${nextBonus.toFixed(2)}`, infoX, infoY + 110);
    } else {
      ctx.fillStyle = '#ff88ff';
      ctx.fillText(t('ui.maxLevel'), infoX, infoY + 20);
    }

    // Enhancement buttons
    const buttonY = panelY + 350;
    const buttonWidth = 150;
    const buttonHeight = 50;

    // Regular enhance
    ctx.fillStyle = '#2a4a2a';
    ctx.fillRect(panelX + 100, buttonY, buttonWidth, buttonHeight);
    ctx.strokeStyle = '#4a8a4a';
    ctx.lineWidth = 2;
    ctx.strokeRect(panelX + 100, buttonY, buttonWidth, buttonHeight);

    ctx.fillStyle = '#88ff88';
    ctx.font = 'bold 14px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(t('crafting.enhance'), panelX + 100 + buttonWidth / 2, buttonY + 25);
    ctx.fillStyle = '#aaaaaa';
    ctx.font = '10px Arial';
    ctx.fillText(t('crafting.enhanceNormal'), panelX + 100 + buttonWidth / 2, buttonY + 40);

    // Protected enhance
    const protAmount = this.getCurrencyCount('koruma_muhru');
    ctx.fillStyle = protAmount > 0 ? '#2a2a4a' : '#1a1a2a';
    ctx.fillRect(panelX + 280, buttonY, buttonWidth, buttonHeight);
    ctx.strokeStyle = protAmount > 0 ? '#4a4a8a' : '#2a2a4a';
    ctx.strokeRect(panelX + 280, buttonY, buttonWidth, buttonHeight);

    ctx.fillStyle = protAmount > 0 ? '#8888ff' : '#444488';
    ctx.font = 'bold 14px Arial';
    ctx.fillText(t('crafting.enhanceProtected'), panelX + 280 + buttonWidth / 2, buttonY + 25);
    ctx.fillStyle = '#aaaaaa';
    ctx.font = '10px Arial';
    ctx.fillText(`🛡️ x${protAmount}`, panelX + 280 + buttonWidth / 2, buttonY + 40);

    // Hammer enhance
    const hammerAmount = this.getCurrencyCount('guclu_cekic');
    ctx.fillStyle = hammerAmount > 0 ? '#4a4a2a' : '#2a2a1a';
    ctx.fillRect(panelX + 190, buttonY + 60, buttonWidth, buttonHeight);
    ctx.strokeStyle = hammerAmount > 0 ? '#8a8a4a' : '#4a4a2a';
    ctx.strokeRect(panelX + 190, buttonY + 60, buttonWidth, buttonHeight);

    ctx.fillStyle = hammerAmount > 0 ? '#ffff88' : '#888844';
    ctx.font = 'bold 14px Arial';
    ctx.fillText(t('crafting.enhanceHammer'), panelX + 190 + buttonWidth / 2, buttonY + 85);
    ctx.fillStyle = '#aaaaaa';
    ctx.font = '10px Arial';
    ctx.fillText(`🔨 x${hammerAmount}`, panelX + 190 + buttonWidth / 2, buttonY + 100);
  }

  private renderItemSelection(ctx: CanvasRenderingContext2D, panelX: number, areaY: number): void {
    // Area background
    ctx.fillStyle = 'rgba(30, 28, 25, 0.9)';
    ctx.fillRect(panelX + 10, areaY, this.PANEL_WIDTH - 20, 200);

    ctx.strokeStyle = '#3d3429';
    ctx.lineWidth = 1;
    ctx.strokeRect(panelX + 10, areaY, this.PANEL_WIDTH - 20, 200);

    // Item label
    ctx.fillStyle = '#888888';
    ctx.font = '12px Arial';
    ctx.textAlign = 'left';
    ctx.fillText(t('crafting.selectItemPrompt'), panelX + 20, areaY - 5);

    // Render inventory items (equipment only)
    const slotSize = 48;
    const padding = 4;
    const cols = 10;
    const startX = panelX + 20;

    const items = this.inventory.getAllItems();

    for (let i = 0; i < Math.min(items.length, 40); i++) {
      const item = items[i];
      if (!item || !('slot' in item)) continue;

      const equipItem = item as EquipmentItem;
      const col = i % cols;
      const row = Math.floor(i / cols);
      const slotX = startX + col * (slotSize + padding);
      const slotY = areaY + 10 + row * (slotSize + padding);

      const isSelected = this.selectedItemIndex === i;

      // Slot background
      ctx.fillStyle = isSelected ? 'rgba(100, 80, 50, 0.9)' : 'rgba(40, 35, 30, 0.9)';
      ctx.fillRect(slotX, slotY, slotSize, slotSize);

      // Rarity border
      const rarityColor = getItemColor(equipItem.rarity);
      ctx.strokeStyle = isSelected ? '#ffd700' : rarityColor;
      ctx.lineWidth = isSelected ? 3 : 2;
      ctx.strokeRect(slotX, slotY, slotSize, slotSize);

      // Item color
      ctx.fillStyle = rarityColor;
      ctx.fillRect(slotX + 8, slotY + 8, slotSize - 16, slotSize - 16);

      // Enhancement level
      if (equipItem.enhancement && equipItem.enhancement > 0) {
        const enhColor = CraftingSystem.getEnhancementColor(equipItem.enhancement);
        ctx.fillStyle = enhColor;
        ctx.font = 'bold 10px Arial';
        ctx.textAlign = 'right';
        ctx.fillText(`+${equipItem.enhancement}`, slotX + slotSize - 3, slotY + slotSize - 3);
      }
    }
  }

  private renderSelectedItem(ctx: CanvasRenderingContext2D, panelX: number, panelY: number): void {
    if (!this.selectedItem) return;

    const infoX = panelX + 350;
    const infoY = panelY + 60;

    // Item name
    const rarityColor = getItemColor(this.selectedItem.rarity);
    ctx.fillStyle = rarityColor;
    ctx.font = 'bold 14px Arial';
    ctx.textAlign = 'left';

    let displayName = this.selectedItem.name;
    if (this.selectedItem.enhancement && this.selectedItem.enhancement > 0) {
      const enhText = CraftingSystem.getEnhancementText(this.selectedItem.enhancement);
      displayName = `${enhText} ${displayName}`;
    }
    ctx.fillText(displayName, infoX, infoY);

    // Rarity
    ctx.fillStyle = '#888888';
    ctx.font = '12px Arial';
    ctx.fillText(this.getRarityName(this.selectedItem.rarity), infoX, infoY + 20);

    // Mods
    let y = infoY + 45;

    // Implicit
    for (const mod of this.selectedItem.implicit) {
      ctx.fillStyle = '#8888ff';
      ctx.fillText(this.formatMod(mod), infoX, y);
      y += 15;
    }

    // Prefixes
    for (const mod of this.selectedItem.prefixes) {
      ctx.fillStyle = '#88ff88';
      ctx.fillText(this.formatMod(mod), infoX, y);
      y += 15;
    }

    // Suffixes
    for (const mod of this.selectedItem.suffixes) {
      ctx.fillStyle = '#88ff88';
      ctx.fillText(this.formatMod(mod), infoX, y);
      y += 15;
    }
  }

  private renderResultMessage(ctx: CanvasRenderingContext2D, panelX: number, panelY: number): void {
    const alpha = Math.min(1, this.resultTimer);

    ctx.save();
    ctx.globalAlpha = alpha;

    ctx.fillStyle = this.resultSuccess ? '#44ff44' : '#ff4444';
    ctx.font = 'bold 18px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(this.resultMessage, panelX + this.PANEL_WIDTH / 2, panelY + this.PANEL_HEIGHT - 60);

    ctx.restore();
  }

  private renderCraftAnimation(ctx: CanvasRenderingContext2D, panelX: number, panelY: number): void {
    const progress = this.craftAnimTimer / 0.5;
    const centerX = panelX + this.PANEL_WIDTH / 2;
    const centerY = panelY + this.PANEL_HEIGHT / 2;

    // Flash effect
    ctx.save();
    ctx.globalAlpha = 1 - progress;

    const gradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, 150);
    gradient.addColorStop(0, this.resultSuccess ? 'rgba(100, 255, 100, 0.8)' : 'rgba(255, 100, 100, 0.8)');
    gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');

    ctx.fillStyle = gradient;
    ctx.fillRect(panelX, panelY, this.PANEL_WIDTH, this.PANEL_HEIGHT);

    ctx.restore();
  }

  private getRarityName(rarity: string): string {
    const key = `items.rarity.${rarity}`;
    const translated = t(key);
    return translated !== key ? translated : rarity;
  }

  private formatMod(mod: { type: string; value: number }): string {
    const names: Record<string, string> = {
      damage: '+{0} Hasar',
      maxHp: '+{0} Can',
      maxMana: '+{0} Mana',
      armor: '+{0} Zırh',
      evasion: '+{0} Kaçış',
      energyShield: '+{0} Enerji Kalkanı',
      strength: '+{0} Güç',
      agility: '+{0} Çeviklik',
      spirit: '+{0} Ruh',
      critChance: '+{0}% Kritik',
      moveSpeed: '+{0}% Hareket Hızı',
      attackSpeed: '+{0}% Saldırı Hızı',
      spellDamage: '+{0} Büyü Hasarı',
      allResist: '+{0} Tüm Dirençler',
      critDamage: '+{0}% Kritik Hasarı',
      odDamage: '+{0}% Od Hasarı',
      suDamage: '+{0}% Su Hasarı',
      gokDamage: '+{0}% Gök Hasarı',
      odResist: '+{0} Od Direnci',
      suResist: '+{0} Su Direnci',
      gokResist: '+{0} Gök Direnci',
      karanlikResist: '+{0} Karanlık Direnci',
      lifeRegen: '+{0} Can Yenileme',
      manaRegen: '+{0} Mana Yenileme',
      lifeLeech: '+{0}% Can Emme',
      manaLeech: '+{0}% Mana Emme',
    };

    const template = names[mod.type] || `+{0} ${mod.type}`;
    return template.replace('{0}', mod.value.toString());
  }
}
